'use client';

import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@routly/ui';
import { Play } from 'lucide-react';
import { useState } from 'react';
import { usePreviewSensorMapping } from '@/hooks/useSensorMappings';
import type { SensorDefinitions } from '@/lib/types';

/**
 * The mapping applied to one raw message.
 *
 * A mapping is written against a device's documentation and the documentation
 * is often wrong. Without this the first sign of a mistake is an empty report
 * a week later, by which time the telemetry it was computed from has expired.
 */
export function MappingPreview({ definitions }: { definitions: SensorDefinitions }) {
  const [raw, setRaw] = useState('{\n  "din": 32980,\n  "io_270": 500\n}');
  const [parseError, setParseError] = useState<string | null>(null);
  const preview = usePreviewSensorMapping();

  const run = () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setParseError('That is not valid JSON');
      return;
    }
    setParseError(null);
    preview.mutate({ definitions, rawData: parsed });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Try it against a message</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <textarea
          aria-label="Raw message"
          className="h-40 w-full rounded-md border border-border bg-background p-3 font-mono text-xs text-foreground"
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
        />

        <Button size="sm" onClick={run} disabled={preview.isPending}>
          <Play className="mr-2 h-4 w-4" />
          Resolve
        </Button>

        {parseError ? <p className="text-sm text-destructive">{parseError}</p> : null}

        {preview.data ? (
          <ul className="space-y-1">
            {preview.data.data.map((row) => (
              <li key={row.name} className="flex items-center justify-between text-sm">
                <span className="font-mono text-muted-foreground">${row.name}</span>
                {row.value === null ? (
                  // Null is the answer that matters: the message does not carry
                  // what this sensor names, which is exactly what somebody is
                  // here to find out.
                  <Badge variant="secondary">null</Badge>
                ) : (
                  <span className="font-mono text-foreground">{String(row.value)}</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
